import {
  BRIDGE_CALLBACK_MARKER,
  BRIDGE_LISTENER_HANDLE_MARKER,
  PLUGIN_METHOD_PREFIX,
  PLUGIN_REMOVE_LISTENER,
  pluginCallbackEventType,
} from '@/pwa/bridge-v2/protocol';
import type { Bridge } from '../bridge';

// =============================================================================
// bridge.plugin('Name') — second-class plugin proxy
// =============================================================================
// Customer-facing Proxy that turns property access on bridge.plugin('Name')
// into bridge.request('plugin:Name.<method>', { args }) calls. Two pieces of
// transparent plumbing:
//
//   1. Outbound: any function in the args (at any depth) is replaced with
//      a callback marker; the SDK subscribes to plugin:callback:<cbId>
//      events so the customer's function fires when the host invokes the
//      reconstructed callback natively.
//
//   2. Inbound: when the response payload matches
//      `{ [BRIDGE_LISTENER_HANDLE_MARKER]: <id> }` (a Capacitor
//      PluginListenerHandle was stashed host-side), wrap it as
//      `{ remove: () => Promise<void> }` so customer code sees the
//      standard handle shape. Calling .remove() round-trips back via
//      Plugin.removeListener and tears down local callback subscriptions.

/** Minimal Capacitor PluginListenerHandle shape — what the SDK returns from
 *  addListener-style calls. Mirrors what calling Capacitor directly would
 *  return, so customer code reads identically. */
export interface PluginListenerHandle {
  remove(): Promise<void>;
}

/** Loose typed proxy — customers can cast to a typed surface if they want.
 *  Property access returns an async function that calls the named method. */
export type PluginProxy = Record<string, (...args: unknown[]) => Promise<unknown>>;

interface CallbackBinding {
  cbId: string;
  unsubscribe: () => void;
}

// Per-page-load monotonic id generator. Only needs to be unique within the
// SDK's own subscription map for this page.
let cbCounter = 0;
const newCbId = (): string => `${Date.now().toString(36)}-${(++cbCounter).toString(36)}`;

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && (v as object).constructor === Object;

// Deep-walk: any function value becomes a callback marker + a bridge.on
// subscription so the customer's function fires on plugin:callback:<cbId>.
// Walks arrays + plain objects; passes Maps, Dates, typed arrays, class
// instances through untouched.
const markAndSubscribe = (
  value: unknown,
  bridge: Bridge,
  bindings: CallbackBinding[],
): unknown => {
  if (typeof value === 'function') {
    const cbId = `cb-${newCbId()}`;
    const cb = value as (...args: unknown[]) => unknown;
    const unsubscribe = bridge.on<unknown[]>(pluginCallbackEventType(cbId), (callArgs) => {
      try {
        if (Array.isArray(callArgs)) cb(...callArgs);
        else cb(callArgs); // defensive — protocol always sends an array
      } catch (err) {
        console.error('[AppbuildWrapper] plugin callback threw:', err);
      }
    });
    bindings.push({ cbId, unsubscribe });
    return { [BRIDGE_CALLBACK_MARKER]: cbId };
  }
  if (Array.isArray(value)) {
    return value.map(v => markAndSubscribe(v, bridge, bindings));
  }
  if (!isPlainObject(value)) return value;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value)) {
    out[k] = markAndSubscribe(v, bridge, bindings);
  }
  return out;
};

interface ListenerHandleMarker {
  [BRIDGE_LISTENER_HANDLE_MARKER]: string;
}

const isListenerHandleMarker = (v: unknown): v is ListenerHandleMarker =>
  !!v
    && typeof v === 'object'
    && typeof (v as Record<string, unknown>)[BRIDGE_LISTENER_HANDLE_MARKER] === 'string';

// Wraps a `{ __bridgeListenerHandle: id }` response payload as a real
// PluginListenerHandle whose .remove() round-trips to the host and tears
// down local subscriptions. Non-marker responses pass through as-is.
//
// Callback-binding lifetime rule: bindings created for a call persist until
// `handle.remove()` (listener path), a request error (torn down in the
// caller's catch), or page unload. We deliberately do NOT tear bindings down
// when a response carries no listener handle: some plugins take a callback
// on a method that resolves immediately and keep invoking it indefinitely —
// e.g. `BackgroundGeolocation.start(options, callback)` resolves
// Promise<void> but delivers position updates through the callback for the
// watcher's lifetime. Tearing down on resolve would silently drop every
// later invocation. This matches direct-Capacitor semantics, where the
// plugin holds your function until you explicitly stop it. Cost: a callback
// passed to a genuinely one-shot method leaves one inert subscription
// behind (bounded, rare — Capacitor one-shot methods don't take callbacks).
const wrapListenerHandle = (
  payload: unknown,
  bridge: Bridge,
  bindings: CallbackBinding[],
): unknown => {
  if (isListenerHandleMarker(payload)) {
    const handleId = payload[BRIDGE_LISTENER_HANDLE_MARKER];
    let removed = false;
    const handle: PluginListenerHandle = {
      async remove(): Promise<void> {
        if (removed) return; // idempotent — safe to double-remove
        removed = true;
        for (const b of bindings) b.unsubscribe();
        await bridge.request<void>(PLUGIN_REMOVE_LISTENER, { id: handleId });
      },
    };
    return handle;
  }
  return payload;
};

/** Returns a Proxy where any property access becomes an async method that
 *  forwards through the bridge as `plugin:<name>.<prop>`. */
export const createPluginProxy = (pluginName: string, bridge: Bridge): PluginProxy => {
  return new Proxy<PluginProxy>({} as PluginProxy, {
    get(_target, prop) {
      if (typeof prop !== 'string') return undefined;
      // Defence against Promise/thenable probes (`Promise.resolve(proxy)`
      // checks `.then`) and symbol-named introspection. Returning
      // undefined keeps the proxy from looking thenable.
      if (prop === 'then') return undefined;

      return async (...args: unknown[]): Promise<unknown> => {
        const bindings: CallbackBinding[] = [];
        const wireArgs = args.map(arg => markAndSubscribe(arg, bridge, bindings));
        const method = `${PLUGIN_METHOD_PREFIX}${pluginName}.${prop}`;
        try {
          const result = await bridge.request<unknown>(method, { args: wireArgs });
          return wrapListenerHandle(result, bridge, bindings);
        } catch (err) {
          // Method failed (PLUGIN_NOT_AVAILABLE, INTERNAL_ERROR, TIMEOUT,
          // etc.) — tear down callback subscriptions since the host won't
          // be invoking them.
          for (const b of bindings) b.unsubscribe();
          throw err;
        }
      };
    },
  });
};
